import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import QMSLayout from "../components/QMSLayout";
import { API } from "../App";
import {
  BookOpen, ChevronLeft, ChevronRight, Loader2, Lock,
  List, Lightbulb, FlaskConical, AlertTriangle, ArrowLeft,
  Globe, ExternalLink, Eye, FileText, BarChart3, Beaker,
  Table2, Network, ClipboardCheck, Building2,
  Shield, Workflow, Users, Compass, Code2, Radio
} from "lucide-react";
import InlineCalculator from "../components/InlineCalculator";
import { useTranslation } from "react-i18next";

/* ─── Page-turner views ─────────────────────────────────────────── */
const VIEW_COVER = "cover";
const VIEW_TOC = "toc";
const VIEW_CHAPTER = "chapter";

/* ─── Page flip styles ──────────────────────────────────────────── */
const pageFlipStyles = `
  .page-flip-container {
    perspective: 2000px;
    perspective-origin: center center;
  }
  .page-content {
    transform-origin: center center;
    backface-visibility: hidden;
  }
  .page-enter {
    animation: pageFlipIn 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }
  .page-exit-right {
    animation: pageFlipOutRight 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }
  .page-exit-left {
    animation: pageFlipOutLeft 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }
  @keyframes pageFlipIn {
    0% { opacity: 0; transform: rotateY(-12deg) scale(0.95) translateX(30px); }
    60% { opacity: 1; }
    100% { opacity: 1; transform: rotateY(0deg) scale(1) translateX(0); }
  }
  @keyframes pageFlipOutRight {
    0% { opacity: 1; transform: rotateY(0deg) scale(1) translateX(0); }
    100% { opacity: 0; transform: rotateY(15deg) scale(0.92) translateX(-60px); }
  }
  @keyframes pageFlipOutLeft {
    0% { opacity: 1; transform: rotateY(0deg) scale(1) translateX(0); }
    100% { opacity: 0; transform: rotateY(-15deg) scale(0.92) translateX(60px); }
  }
`;

/* ═══════════════════════════════════════════════════════════════════
   READER COVER — Single clean image component (all books)
   Images are pre-trimmed via pycover_cli.py, no CSS tricks needed
   ═══════════════════════════════════════════════════════════════════ */
const ReaderCoverPage = ({ coverImage, altText, onTurnPage }) => (
  <div
    className="relative mx-auto w-full max-w-2xl overflow-hidden cursor-pointer group"
    onClick={onTurnPage}
    data-testid="cover-page"
    style={{
      aspectRatio: "2 / 3",
      boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
    }}
  >
    <img src={coverImage} alt={altText} className="w-full h-full object-cover" />
    {/* Turn page hint */}
    <div className="absolute bottom-6 right-6 z-20 opacity-0 group-hover:opacity-100 transition-opacity">
      <div className="bg-black/50 text-white text-[10px] px-3 py-1.5 rounded flex items-center gap-1">
        Turn Page <ChevronRight className="w-3 h-3" />
      </div>
    </div>
    {/* Spine */}
    <div className="absolute left-0 top-0 bottom-0 w-[8px] z-30"
      style={{ background: "linear-gradient(90deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.1) 40%, transparent 100%)" }} />
  </div>
);

const EBookReaderPage = () => {
  const { t } = useTranslation('platform');
  const { bookId } = useParams();
  const navigate = useNavigate();

  const [view, setView] = useState(VIEW_COVER);
  const [bookData, setBookData] = useState(null);
  const [chapter, setChapter] = useState(null);
  const [currentChapter, setCurrentChapter] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flipClass, setFlipClass] = useState("page-enter");
  const [transitioning, setTransitioning] = useState(false);
  const flipTimer = useRef(null);

  const doFlip = useCallback((direction, callback) => {
    if (transitioning) return;
    setTransitioning(true);
    setFlipClass(direction === "forward" ? "page-exit-right" : "page-exit-left");
    flipTimer.current = setTimeout(() => {
      callback();
      setFlipClass("page-enter");
      setTransitioning(false);
      window.scrollTo(0, 0);
    }, 450);
  }, [transitioning]);

  useEffect(() => () => { if (flipTimer.current) clearTimeout(flipTimer.current); }, []);

  const fetchBook = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/publishing/book/${bookId}`, { credentials: "include" });
      if (res.ok) {
        setBookData(await res.json());
      } else {
        const err = await res.json();
        setError(err.detail || t('platform:ebook.bookNotFound'));
      }
    } catch { setError(t('platform:ebook.failedLoadBook')); }
    setLoading(false);
  }, [bookId]);

  const fetchChapter = useCallback(async (chNum) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/publishing/book/${bookId}/chapter/${chNum}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setChapter(data);
        setCurrentChapter(chNum);
        setView(VIEW_CHAPTER);
      } else {
        const err = await res.json();
        setError(err.detail || "Chapter not found");
      }
    } catch { setError("Failed to load chapter"); }
    setLoading(false);
  }, [bookId]);

  useEffect(() => { fetchBook(); }, [fetchBook]);

  const turnToToc = () => doFlip("forward", () => setView(VIEW_TOC));

  const openChapter = (chNum) => doFlip("forward", () => fetchChapter(chNum));

  const goBack = () => {
    if (view === VIEW_CHAPTER) { doFlip("backward", () => { setView(VIEW_TOC); setChapter(null); }); }
    else if (view === VIEW_TOC) { doFlip("backward", () => setView(VIEW_COVER)); }
    else { navigate("/publishing"); }
  };

  if (loading && !bookData) {
    return (
      <QMSLayout title={t('platform:ebook.title')}>
        <div className="flex justify-center h-64 items-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#00C8E0]" />
        </div>
      </QMSLayout>
    );
  }

  if (error && !bookData) {
    return (
      <QMSLayout title={t('platform:ebook.title')}>
        <div className="flex flex-col items-center justify-center h-64 gap-4" data-testid="reader-error">
          <Lock className="w-12 h-12 text-red-400" />
          <p className="text-red-400 text-sm">{error}</p>
          <button onClick={() => navigate("/publishing")} className="text-[#00C8E0] hover:text-white text-sm flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back to Publishing Portal
          </button>
        </div>
      </QMSLayout>
    );
  }

  /* ═══════════════════════════════════════════════════════════════
     COVER VIEW
     ═══════════════════════════════════════════════════════════════ */
  if (view === VIEW_COVER && bookData) {
    const renderCover = () => {
      if (bookData.cover_image) {
        return <ReaderCoverPage coverImage={bookData.cover_image} altText={bookData.title} onTurnPage={turnToToc} />;
      }
      return <ReaderCoverPage coverImage="/assets/covers/book_I_cover.png" altText={bookData.title} onTurnPage={turnToToc} />;
    };

    return (
      <QMSLayout title={t('platform:ebook.title')}>
        <style>{pageFlipStyles}</style>
        <div className="max-w-3xl mx-auto space-y-4 page-flip-container" data-testid="reader-cover-view">
          <button onClick={() => navigate("/publishing")} className="text-slate-400 hover:text-white text-xs flex items-center gap-1">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Bookshelf
          </button>

          <div className={`py-4 page-content ${flipClass}`}>
            {renderCover()}
          </div>

          <div className="text-center">
            <button onClick={turnToToc} data-testid="turn-to-toc" disabled={transitioning}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#1A6BCC] hover:bg-[#2178DD] text-white text-xs font-bold uppercase rounded-[3px] transition-colors disabled:opacity-50">
              Open Book <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </QMSLayout>
    );
  }

  /* ═══════════════════════════════════════════════════════════════
     TABLE OF CONTENTS VIEW
     ═══════════════════════════════════════════════════════════════ */
  if (view === VIEW_TOC && bookData) {
    return (
      <QMSLayout title={t('platform:ebook.title')}>
        <style>{pageFlipStyles}</style>
        <div className="max-w-3xl mx-auto space-y-5 page-flip-container" data-testid="reader-toc">
          <div className="flex items-center justify-between">
            <button onClick={goBack} disabled={transitioning} className="text-slate-400 hover:text-white text-xs flex items-center gap-1 disabled:opacity-50">
              <ArrowLeft className="w-3.5 h-3.5" /> Cover
            </button>
            <span className="text-slate-500 text-[10px] uppercase tracking-wider">Table of Contents</span>
          </div>

          <div className={`page-content ${flipClass}`}>
            <div className="bg-gradient-to-b from-[#132340] to-[#0A1628] border border-[#1E3A5F] rounded-[3px] p-8 text-center mb-5">
              <div className="text-[10px] text-[#00C8E0] uppercase tracking-[4px] mb-3">{bookData.series}</div>
              <h1 className="text-2xl font-bold text-white mb-2" data-testid="reader-book-title">{bookData.title}</h1>
              <p className="text-slate-400 text-sm">{bookData.subtitle}</p>
              <p className="text-slate-500 text-xs mt-3">by {bookData.author}</p>
              <div className="mt-4 flex items-center justify-center gap-3">
                <span className="inline-block bg-emerald-500/10 text-emerald-400 text-[10px] px-3 py-1 rounded-full uppercase font-bold tracking-wide">
                  Free 2-Page Preview
                </span>
                <span className="text-slate-600 text-[10px]">ISBN: {bookData.isbn}</span>
              </div>
            </div>

            <div className="bg-amber-500/5 border border-amber-500/20 rounded-[3px] p-3 text-xs text-amber-400/80 text-center mb-5" data-testid="watermark-notice">
              {bookData.watermark} — This preview is protected and licensed for personal use only.
            </div>

            <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-6">
              <h2 className="text-white font-bold text-sm mb-4 flex items-center gap-2">
                <List className="w-4 h-4 text-[#00C8E0]" /> Chapters
              </h2>
              <div className="space-y-1">
                {bookData.chapters.map((ch) => (
                  <button
                    key={ch.chapter_num}
                    onClick={() => openChapter(ch.chapter_num)}
                    disabled={transitioning}
                    className="w-full text-left p-3.5 rounded-[3px] hover:bg-[#1A6BCC]/10 transition-colors group disabled:opacity-50"
                    data-testid={`toc-chapter-${ch.chapter_num}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <span className="text-[#00C8E0] font-mono text-xs w-7 shrink-0 mt-0.5">{ch.chapter_num}.</span>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-slate-200 text-sm group-hover:text-white font-medium">{ch.title}</span>
                            {ch.is_bonus && (
                              <span className="text-[9px] bg-amber-500/10 text-amber-400 px-1.5 py-0.5 rounded font-bold uppercase shrink-0">Bonus</span>
                            )}
                          </div>
                          <p className="text-slate-500 text-xs mt-1 leading-relaxed line-clamp-2">{ch.abstract}</p>
                          <span className="text-[10px] text-slate-600 mt-1 inline-block">{ch.category}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 mt-1">
                        <Eye className="w-3.5 h-3.5 text-slate-600 group-hover:text-[#00C8E0]" />
                        <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-[#00C8E0]" />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </QMSLayout>
    );
  }

  /* ═══════════════════════════════════════════════════════════════
     CHAPTER PREVIEW VIEW
     ═══════════════════════════════════════════════════════════════ */
  if (view === VIEW_CHAPTER && chapter) {
    const ps = chapter.preview_summary;
    const marketplaces = chapter.marketplaces || {};

    return (
      <QMSLayout title={t('platform:ebook.title')}>
        <style>{pageFlipStyles}</style>
        <div className="max-w-3xl mx-auto page-flip-container" data-testid="reader-chapter">
          <style>{`
            .ebook-content {
              -webkit-user-select: none; -moz-user-select: none; -ms-user-select: none; user-select: none;
            }
            @media print {
              .ebook-content { display: none !important; }
              .ebook-print-block::after {
                content: "Printing is not permitted. This content is protected by Charles Muchina / CHARSTAN.";
                display: block; font-size: 24px; text-align: center; padding: 100px;
              }
            }
          `}</style>

          <div className="flex items-center justify-between mb-4">
            <button onClick={goBack} disabled={transitioning} className="text-slate-400 hover:text-white text-xs flex items-center gap-1 disabled:opacity-50" data-testid="back-to-toc">
              <ArrowLeft className="w-3.5 h-3.5" /> Table of Contents
            </button>
            <div className="flex items-center gap-3">
              <span className="text-[10px] bg-amber-500/10 text-amber-400 px-2 py-0.5 rounded font-bold uppercase">Preview</span>
              <span className="text-slate-500 text-xs">Chapter {chapter.chapter_num} of {chapter.total_chapters}</span>
            </div>
          </div>

          {loading && (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#00C8E0]" /></div>
          )}

          {!loading && (
            <div className={`ebook-content ebook-print-block relative page-content ${flipClass}`} onContextMenu={(e) => e.preventDefault()}>
              <div className="pointer-events-none fixed inset-0 z-10 flex items-center justify-center opacity-[0.03] rotate-[-30deg]" data-testid="watermark-overlay">
                <div className="text-white text-6xl font-bold whitespace-nowrap select-none">
                  Charles Muchina / CHARSTAN
                </div>
              </div>

              <div className="bg-gradient-to-r from-[#132340] to-[#0F1E35] border border-[#1E3A5F] rounded-[3px] p-6 mb-4" data-testid="chapter-page-1">
                <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">{chapter.book_title}</div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="text-xs text-[#00C8E0] uppercase tracking-wider font-bold">Chapter {chapter.chapter_num}</div>
                  <span className="text-slate-700">&middot;</span>
                  <span className="text-[10px] text-slate-500">{chapter.category}</span>
                </div>
                <h1 className="text-xl font-bold text-white mb-4" data-testid="chapter-title">{chapter.chapter_title}</h1>
                <div className="bg-[#0A1628]/50 border-l-2 border-[#1A6BCC] p-4 rounded-r-[3px]" data-testid="chapter-abstract">
                  <div className="text-[10px] text-[#1A6BCC] uppercase tracking-wider font-bold mb-1">Abstract</div>
                  <p className="text-slate-300 text-sm leading-relaxed italic">{chapter.abstract}</p>
                </div>
              </div>

              {chapter.intro && (
                <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-5 mb-4">
                  <p className="text-slate-300 text-sm leading-relaxed" data-testid="chapter-intro">{chapter.intro}</p>
                </div>
              )}

              {chapter.concepts?.length > 0 && (
                <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-5 mb-4">
                  <h3 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-amber-400" /> Key Concepts
                  </h3>
                  <div className="space-y-3" data-testid="chapter-concepts">
                    {chapter.concepts.map((c, i) => (
                      <div key={i} className="flex gap-3">
                        <span className="text-[#00C8E0] font-mono text-xs mt-0.5 shrink-0">{i + 1}.</span>
                        <p className="text-slate-300 text-sm leading-relaxed">{c}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {chapter.formulas?.length > 0 && (
                <div className="bg-[#0F1E35] border border-[#1E3A5F] rounded-[3px] p-5 mb-4">
                  <h3 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
                    <FlaskConical className="w-4 h-4 text-[#00C8E0]" /> Key Formulas
                  </h3>
                  <div className="space-y-3" data-testid="chapter-formulas">
                    {chapter.formulas.map((f, i) => (
                      <div key={i} className="bg-[#132340] rounded-[3px] p-3">
                        <div className="text-white text-sm font-bold">{f.name}</div>
                        <div className="text-[#00C8E0] font-mono text-xs mt-1" dangerouslySetInnerHTML={{ __html: f.formula }} />
                        <div className="text-slate-500 text-[10px] mt-1">{f.standard}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ═══ ENRICHMENT CONTENT (AI-generated deep technical material) ═══ */}
              {chapter.enrichment && (
                <div className="space-y-4" data-testid="enrichment-content">

                  {/* Technical Background & Product Lifecycle */}
                  {chapter.enrichment.technical_background && (
                    <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-5" data-testid="technical-background">
                      <h3 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-emerald-400" /> Technical Background
                      </h3>
                      <p className="text-slate-300 text-sm leading-relaxed mb-4 whitespace-pre-line">{chapter.enrichment.technical_background.engineering_context}</p>

                      {chapter.enrichment.technical_background.lifecycle_relevance && (
                        <div className="space-y-2">
                          <div className="text-[10px] text-emerald-400 uppercase tracking-wider font-bold mb-2">Product Lifecycle Relevance</div>
                          {Object.entries(chapter.enrichment.technical_background.lifecycle_relevance).map(([phase, desc]) => (
                            <div key={phase} className="bg-[#0A1628] border-l-2 border-emerald-500/40 rounded-r-[3px] p-3">
                              <div className="text-emerald-400 text-[10px] uppercase tracking-wider font-bold mb-1">
                                {phase.replace(/_/g, " ")}
                              </div>
                              <p className="text-slate-400 text-xs leading-relaxed">{desc}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {chapter.enrichment.technical_background.industry_sectors?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-3">
                          {chapter.enrichment.technical_background.industry_sectors.map((s, i) => (
                            <span key={i} className="text-[9px] bg-emerald-500/10 text-emerald-400 px-2 py-0.5 rounded font-medium">{s}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Case Studies */}
                  {chapter.enrichment.case_studies?.length > 0 && (
                    <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-5" data-testid="case-studies">
                      <h3 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
                        <FileText className="w-4 h-4 text-amber-400" /> Applications & Case Studies
                      </h3>
                      <div className="space-y-4">
                        {chapter.enrichment.case_studies.map((cs, i) => (
                          <div key={i} className="bg-[#0A1628] border border-[#1E3A5F]/60 rounded-[3px] p-4">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-white text-sm font-bold">{cs.title}</h4>
                              <span className="text-[9px] bg-amber-500/10 text-amber-400 px-2 py-0.5 rounded font-medium">{cs.industry}</span>
                            </div>
                            <div className="space-y-2 text-xs">
                              <div><span className="text-[#00C8E0] font-bold">Scenario:</span> <span className="text-slate-400">{cs.scenario}</span></div>
                              <div><span className="text-[#00C8E0] font-bold">Approach:</span> <span className="text-slate-400">{cs.approach}</span></div>
                              <div><span className="text-emerald-400 font-bold">Outcome:</span> <span className="text-slate-300">{cs.outcome}</span></div>
                              <div className="bg-[#132340] border-l-2 border-amber-500/40 rounded-r-[3px] p-2 mt-1">
                                <span className="text-amber-400 font-bold text-[10px]">Lesson:</span> <span className="text-slate-400">{cs.lessons_learned}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Analytical Assessment & Testing */}
                  {chapter.enrichment.analytical_assessment && (
                    <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-5" data-testid="analytical-assessment">
                      <h3 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
                        <Beaker className="w-4 h-4 text-violet-400" /> Analytical Assessment & Testing
                      </h3>
                      <p className="text-slate-300 text-sm leading-relaxed mb-4 whitespace-pre-line">{chapter.enrichment.analytical_assessment.test_methodology}</p>

                      {chapter.enrichment.analytical_assessment.acceptance_criteria?.length > 0 && (
                        <div className="mb-4">
                          <div className="text-[10px] text-violet-400 uppercase tracking-wider font-bold mb-2">Acceptance Criteria</div>
                          <div className="space-y-1">
                            {chapter.enrichment.analytical_assessment.acceptance_criteria.map((c, i) => (
                              <div key={i} className="flex gap-2 text-xs">
                                <ClipboardCheck className="w-3 h-3 text-violet-400 mt-0.5 shrink-0" />
                                <span className="text-slate-400">{c}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Threshold Table */}
                      {chapter.enrichment.analytical_assessment.threshold_table && (
                        <div className="overflow-x-auto">
                          <div className="text-[10px] text-violet-400 uppercase tracking-wider font-bold mb-2">
                            {chapter.enrichment.analytical_assessment.threshold_table.title}
                          </div>
                          <table className="w-full text-xs" data-testid="threshold-table">
                            <thead>
                              <tr className="border-b border-[#1E3A5F]">
                                {chapter.enrichment.analytical_assessment.threshold_table.headers.map((h, i) => (
                                  <th key={i} className="text-left text-slate-400 font-bold uppercase text-[10px] py-2 px-2">{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {chapter.enrichment.analytical_assessment.threshold_table.rows.map((row, i) => (
                                <tr key={i} className="border-b border-[#1E3A5F]/40">
                                  {row.map((cell, j) => (
                                    <td key={j} className={`py-2 px-2 ${j === 0 ? "text-slate-200 font-medium" : "text-slate-400"}`}>{cell}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Illustrations & Tables */}
                  {chapter.enrichment.illustrations_tables?.length > 0 && (
                    <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-5" data-testid="illustrations-tables">
                      <h3 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
                        <Table2 className="w-4 h-4 text-sky-400" /> Tables & Structured Data
                      </h3>
                      <div className="space-y-4">
                        {chapter.enrichment.illustrations_tables.map((tbl, i) => (
                          <div key={i} className="overflow-x-auto">
                            <div className="text-sky-400 text-xs font-bold mb-2">{tbl.title}</div>
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="border-b border-[#1E3A5F]">
                                  {tbl.headers.map((h, j) => (
                                    <th key={j} className="text-left text-slate-400 font-bold uppercase text-[10px] py-2 px-2">{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {tbl.rows.map((row, j) => (
                                  <tr key={j} className="border-b border-[#1E3A5F]/30">
                                    {row.map((cell, k) => (
                                      <td key={k} className={`py-1.5 px-2 ${k === 0 ? "text-slate-200" : "text-slate-400"}`}>{cell}</td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {tbl.notes && <div className="text-[9px] text-slate-500 mt-1 italic">{tbl.notes}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Data Synthesis & Analysis */}
                  {chapter.enrichment.data_synthesis && (
                    <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-5" data-testid="data-synthesis">
                      <h3 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
                        <Network className="w-4 h-4 text-rose-400" /> Data Synthesis & Analysis
                      </h3>
                      <div className="space-y-3 text-sm text-slate-300 leading-relaxed">
                        <div>
                          <div className="text-rose-400 text-[10px] uppercase tracking-wider font-bold mb-1">Data Collection Methodology</div>
                          <p className="whitespace-pre-line">{chapter.enrichment.data_synthesis.methodology}</p>
                        </div>
                        <div>
                          <div className="text-rose-400 text-[10px] uppercase tracking-wider font-bold mb-1">Analysis Framework</div>
                          <p className="whitespace-pre-line">{chapter.enrichment.data_synthesis.analysis_framework}</p>
                        </div>
                        {chapter.enrichment.data_synthesis.interpretation_guide && (
                          <div>
                            <div className="text-rose-400 text-[10px] uppercase tracking-wider font-bold mb-1">Interpretation Guide</div>
                            <p className="whitespace-pre-line">{chapter.enrichment.data_synthesis.interpretation_guide}</p>
                          </div>
                        )}
                        <div>
                          <div className="text-rose-400 text-[10px] uppercase tracking-wider font-bold mb-1">Presentation Format</div>
                          <p>{chapter.enrichment.data_synthesis.presentation_format}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Computational Chart */}
                  {chapter.enrichment.computational_data && (
                    <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-5" data-testid="computational-chart">
                      <h3 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
                        <BarChart3 className="w-4 h-4 text-[#00C8E0]" /> Computational Chart
                      </h3>
                      <div className="text-[#00C8E0] text-xs font-bold mb-3">{chapter.enrichment.computational_data.chart_title}</div>

                      {/* Static bar chart */}
                      <div className="bg-[#0A1628] border border-[#1E3A5F]/60 rounded-[3px] p-4 mb-3">
                        <div className="flex items-end gap-2 h-40 relative">
                          {/* Y-axis label */}
                          <div className="absolute -left-1 top-0 bottom-0 flex items-center">
                            <span className="text-[8px] text-slate-500 -rotate-90 whitespace-nowrap">{chapter.enrichment.computational_data.y_label}</span>
                          </div>

                          {/* Threshold line */}
                          {chapter.enrichment.computational_data.threshold_line && (
                            <div className="absolute left-6 right-0 border-t border-dashed border-red-400/50"
                              style={{
                                bottom: `${(chapter.enrichment.computational_data.threshold_line.value / Math.max(...chapter.enrichment.computational_data.data_points.map(d => d.value), 100)) * 100}%`
                              }}>
                              <span className="text-[7px] text-red-400 absolute -top-3 right-0">
                                {chapter.enrichment.computational_data.threshold_line.label} ({chapter.enrichment.computational_data.threshold_line.value})
                              </span>
                            </div>
                          )}

                          {/* Bars */}
                          <div className="flex-1 flex items-end justify-around gap-1.5 ml-6">
                            {chapter.enrichment.computational_data.data_points.map((dp, i) => {
                              const maxVal = Math.max(...chapter.enrichment.computational_data.data_points.map(d => d.value), 100);
                              const heightPct = (dp.value / maxVal) * 100;
                              const threshold = chapter.enrichment.computational_data.threshold_line?.value || 0;
                              const barColor = dp.value >= threshold ? "#10B981" : "#EF4444";
                              return (
                                <div key={i} className="flex flex-col items-center gap-1 flex-1">
                                  <span className="text-[8px] text-slate-300 font-bold">{dp.value}</span>
                                  <div className="w-full rounded-t-[2px] transition-all"
                                    style={{ height: `${heightPct}%`, backgroundColor: barColor, minHeight: "4px", maxWidth: "40px" }} />
                                  <span className="text-[7px] text-slate-500 text-center leading-tight w-full truncate">{dp.label}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        <div className="text-center text-[8px] text-slate-500 mt-2">{chapter.enrichment.computational_data.x_label}</div>
                      </div>

                      <p className="text-slate-400 text-xs leading-relaxed">{chapter.enrichment.computational_data.report_summary}</p>
                    </div>
                  )}
                </div>
              )}

              {/* ═══ FRONT MATTER EXTENSIONS (8a-8h) ═══ */}
              {chapter.front_matter_extensions && (
                <div className="space-y-4" data-testid="fme-content">

                  {/* 8a: Standards Crosswalk */}
                  {chapter.front_matter_extensions.standards_crosswalk && (
                    <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-5" data-testid="fme-standards-crosswalk">
                      <h3 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
                        <Shield className="w-4 h-4 text-cyan-400" /> Standards Crosswalk
                      </h3>
                      {chapter.front_matter_extensions.standards_crosswalk.primary_standards?.length > 0 && (
                        <div className="overflow-x-auto mb-4">
                          <table className="w-full text-xs" data-testid="standards-table">
                            <thead>
                              <tr className="border-b border-[#1E3A5F]">
                                <th className="text-left text-slate-400 font-bold uppercase text-[10px] py-2 px-2">Standard</th>
                                <th className="text-left text-slate-400 font-bold uppercase text-[10px] py-2 px-2">Clause</th>
                                <th className="text-left text-slate-400 font-bold uppercase text-[10px] py-2 px-2">Relevance</th>
                              </tr>
                            </thead>
                            <tbody>
                              {chapter.front_matter_extensions.standards_crosswalk.primary_standards.map((s, i) => (
                                <tr key={i} className="border-b border-[#1E3A5F]/30">
                                  <td className="py-2 px-2 text-cyan-400 font-medium">{s.standard}</td>
                                  <td className="py-2 px-2 text-slate-300">{s.clause}</td>
                                  <td className="py-2 px-2 text-slate-400">{s.relevance}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {chapter.front_matter_extensions.standards_crosswalk.combination_product_notes && (
                        <div className="bg-[#0A1628] border-l-2 border-cyan-500/40 rounded-r-[3px] p-3 mb-2">
                          <div className="text-cyan-400 text-[10px] uppercase tracking-wider font-bold mb-1">Combination Products</div>
                          <p className="text-slate-400 text-xs leading-relaxed">{chapter.front_matter_extensions.standards_crosswalk.combination_product_notes}</p>
                        </div>
                      )}
                      {chapter.front_matter_extensions.standards_crosswalk.pharma_interface && (
                        <div className="bg-[#0A1628] border-l-2 border-cyan-500/40 rounded-r-[3px] p-3">
                          <div className="text-cyan-400 text-[10px] uppercase tracking-wider font-bold mb-1">Pharma Interface</div>
                          <p className="text-slate-400 text-xs leading-relaxed">{chapter.front_matter_extensions.standards_crosswalk.pharma_interface}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* 8b: Lifecycle Management Framework */}
                  {chapter.front_matter_extensions.lifecycle_framework && (
                    <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-5" data-testid="fme-lifecycle-framework">
                      <h3 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
                        <Workflow className="w-4 h-4 text-teal-400" /> Lifecycle Management Framework
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                        {["design", "development", "manufacturing", "quality_assurance", "regulatory", "post_market"].map((stage) => {
                          const val = chapter.front_matter_extensions.lifecycle_framework[stage];
                          if (!val) return null;
                          const colors = { design: "text-blue-400", development: "text-indigo-400", manufacturing: "text-orange-400", quality_assurance: "text-green-400", regulatory: "text-red-400", post_market: "text-purple-400" };
                          return (
                            <div key={stage} className="bg-[#0A1628] border border-[#1E3A5F]/50 rounded-[3px] p-3">
                              <div className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${colors[stage] || "text-teal-400"}`}>
                                {stage.replace(/_/g, " ")}
                              </div>
                              <p className="text-slate-400 text-xs leading-relaxed">{val}</p>
                            </div>
                          );
                        })}
                      </div>
                      {chapter.front_matter_extensions.lifecycle_framework.tool_suitability?.length > 0 && (
                        <div>
                          <div className="text-teal-400 text-[10px] uppercase tracking-wider font-bold mb-2">Tool Suitability by Stage</div>
                          <div className="space-y-1.5">
                            {chapter.front_matter_extensions.lifecycle_framework.tool_suitability.map((t, i) => (
                              <div key={i} className="flex items-start gap-2 text-xs">
                                <span className="text-teal-400 font-bold shrink-0">{t.tool}:</span>
                                <span className="text-slate-500">{t.best_stage} —</span>
                                <span className="text-slate-400">{t.rationale}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Roles & Responsibilities */}
                  {chapter.front_matter_extensions.roles_responsibilities?.length > 0 && (
                    <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-5" data-testid="fme-roles">
                      <h3 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
                        <Users className="w-4 h-4 text-amber-400" /> Roles & Responsibilities
                      </h3>
                      <div className="space-y-2">
                        {chapter.front_matter_extensions.roles_responsibilities.map((r, i) => (
                          <div key={i} className="flex items-start gap-3 bg-[#0A1628] border border-[#1E3A5F]/40 rounded-[3px] p-3">
                            <div className="shrink-0">
                              <div className="text-amber-400 text-xs font-bold">{r.role}</div>
                              <div className="text-[9px] text-slate-500">{r.lifecycle_stage}</div>
                            </div>
                            <p className="text-slate-400 text-xs leading-relaxed flex-1">{r.key_actions}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Technique Atlas */}
                  {chapter.front_matter_extensions.technique_atlas && (
                    <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-5" data-testid="fme-technique-atlas">
                      <h3 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
                        <Compass className="w-4 h-4 text-sky-400" /> Technique Atlas
                      </h3>
                      <div className="bg-[#0A1628] border border-[#1E3A5F]/50 rounded-[3px] p-4 mb-3">
                        <div className="text-sky-400 text-xs font-bold mb-2">{chapter.front_matter_extensions.technique_atlas.primary_technique}</div>
                        {chapter.front_matter_extensions.technique_atlas.class_iii_threshold && (
                          <div className="bg-red-500/10 border border-red-500/20 rounded-[3px] p-2 mb-3">
                            <span className="text-red-400 text-[10px] font-bold uppercase">Class III Threshold: </span>
                            <span className="text-slate-300 text-xs">{chapter.front_matter_extensions.technique_atlas.class_iii_threshold}</span>
                          </div>
                        )}
                        {chapter.front_matter_extensions.technique_atlas.lifecycle_touchpoints?.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-3">
                            {chapter.front_matter_extensions.technique_atlas.lifecycle_touchpoints.map((tp, i) => (
                              <span key={i} className="text-[9px] bg-sky-500/10 text-sky-400 px-2 py-0.5 rounded font-medium">{tp}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      {chapter.front_matter_extensions.technique_atlas.cross_volume_refs?.length > 0 && (
                        <div>
                          <div className="text-sky-400 text-[10px] uppercase tracking-wider font-bold mb-2">Cross-Volume References</div>
                          <div className="space-y-1">
                            {chapter.front_matter_extensions.technique_atlas.cross_volume_refs.map((ref, i) => (
                              <div key={i} className="flex gap-2 text-xs">
                                <span className="text-slate-500 shrink-0">{ref.volume} — {ref.chapter}:</span>
                                <span className="text-slate-400">{ref.connection}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* 8g: Python Template References — Interactive Inline Calculators */}
                  {chapter.front_matter_extensions.python_templates?.length > 0 && (
                    <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-5" data-testid="fme-python-templates">
                      <h3 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
                        <Code2 className="w-4 h-4 text-lime-400" /> Engineering Workbench
                      </h3>
                      <p className="text-slate-500 text-[10px] mb-4">Modify inputs and compute results inline. View source code or open the full calculator page.</p>
                      <div className="space-y-3">
                        {chapter.front_matter_extensions.python_templates.map((pt, i) => (
                          <InlineCalculator
                            key={pt.template_id || i}
                            templateId={pt.template_id}
                            contextNote={pt.example_application || pt.relevance}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 8h: Post-Production Review & Field Feedback */}
                  {chapter.front_matter_extensions.post_production && (
                    <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-5" data-testid="fme-post-production">
                      <h3 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
                        <Radio className="w-4 h-4 text-orange-400" /> Post-Production Review & Field Feedback
                      </h3>
                      <div className="space-y-3">
                        {chapter.front_matter_extensions.post_production.field_data_capture && (
                          <div>
                            <div className="text-orange-400 text-[10px] uppercase tracking-wider font-bold mb-1">Field Data Capture</div>
                            <p className="text-slate-400 text-xs leading-relaxed">{chapter.front_matter_extensions.post_production.field_data_capture}</p>
                          </div>
                        )}
                        {chapter.front_matter_extensions.post_production.trending_feedback && (
                          <div>
                            <div className="text-orange-400 text-[10px] uppercase tracking-wider font-bold mb-1">Trending & Feedback Loop</div>
                            <p className="text-slate-400 text-xs leading-relaxed">{chapter.front_matter_extensions.post_production.trending_feedback}</p>
                          </div>
                        )}
                        {chapter.front_matter_extensions.post_production.regulatory_update_cycle && (
                          <div>
                            <div className="text-orange-400 text-[10px] uppercase tracking-wider font-bold mb-1">Regulatory Update Cycle</div>
                            <p className="text-slate-400 text-xs leading-relaxed">{chapter.front_matter_extensions.post_production.regulatory_update_cycle}</p>
                          </div>
                        )}
                        {chapter.front_matter_extensions.post_production.recall_considerations && (
                          <div className="bg-red-500/5 border border-red-500/20 rounded-[3px] p-3">
                            <div className="text-red-400 text-[10px] uppercase tracking-wider font-bold mb-1">Recall & Field Safety Considerations</div>
                            <p className="text-slate-400 text-xs leading-relaxed">{chapter.front_matter_extensions.post_production.recall_considerations}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {chapter.preview_mode && ps && (
                <div className="border-2 border-dashed border-[#1A6BCC]/40 rounded-[3px] overflow-hidden mb-4" data-testid="end-of-preview">
                  <div className="h-10 bg-gradient-to-b from-transparent to-[#0A1628]" />
                  <div className="bg-[#0A1628] px-6 pb-6 pt-2 text-center">
                    <BookOpen className="w-10 h-10 text-[#1A6BCC] mx-auto mb-3" />
                    <h3 className="text-white font-bold text-lg mb-2" data-testid="preview-end-title">End of Preview</h3>
                    <p className="text-slate-400 text-sm mb-4 max-w-md mx-auto">
                      You've reached the end of the free 2-page preview for this chapter.
                    </p>

                    <div className="bg-[#0D1F38] border border-[#00C8E0]/30 rounded-[3px] p-5 mb-5 max-w-md mx-auto" data-testid="reader-hub-cta">
                      <div className="text-[10px] text-[#00C8E0] uppercase tracking-wider mb-2 font-bold">Already own the print edition?</div>
                      <h4 className="text-white font-bold text-sm mb-2">Activate Your Free Lifetime Digital Access</h4>
                      <p className="text-slate-400 text-xs mb-3">Scan the QR code inside your book or enter your access code below.</p>
                      <a
                        href="/readers"
                        className="inline-flex items-center gap-2 bg-[#00C8E0] text-[#0A1628] font-bold text-xs px-5 py-2.5 rounded hover:bg-[#00C8E0]/80 transition-colors"
                        data-testid="reader-hub-cta-link"
                      >
                        Activate at charstan.com/readers <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </div>

                    <p className="text-slate-500 text-xs mb-4">— or purchase a copy —</p>

                    <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-4 mb-5 max-w-md mx-auto" data-testid="locked-content-summary">
                      <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2 font-bold">Remaining in Full Chapter</div>
                      <p className="text-slate-300 text-sm">{ps.locked_content}</p>
                    </div>

                    <p className="text-slate-400 text-xs mb-3">Purchase the full book from a retailer:</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-w-lg mx-auto" data-testid="vendor-links">
                      {Object.entries(marketplaces).map(([key, mp]) => (
                        <div key={key}
                          className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-3 flex items-center gap-2 hover:border-[#1A6BCC]/40 transition-colors"
                          data-testid={`vendor-${key}`}>
                          <Globe className="w-4 h-4 text-[#00C8E0] shrink-0" />
                          <div className="min-w-0">
                            <div className="text-white text-xs font-bold truncate">{mp.name}</div>
                            <div className="text-[10px] text-amber-400 uppercase font-bold">
                              {mp.status === "coming_soon" ? "Coming Soon" : "Available"}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <p className="text-slate-600 text-[10px] mt-4">e-Book and Hardcover editions — ISBN {bookData?.isbn || "assigned"}</p>
                  </div>
                </div>
              )}

              <div className="text-center text-[10px] text-slate-600 py-4 border-t border-[#1E3A5F] mt-4" data-testid="footer-watermark">
                {chapter.watermark}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mt-6 pt-4 border-t border-[#1E3A5F]">
            {chapter.has_prev ? (
              <button onClick={() => openChapter(currentChapter - 1)} disabled={transitioning} className="flex items-center gap-2 text-[#00C8E0] hover:text-white text-sm disabled:opacity-50" data-testid="prev-chapter">
                <ChevronLeft className="w-4 h-4" /> Previous Chapter
              </button>
            ) : <div />}
            {chapter.has_next ? (
              <button onClick={() => openChapter(currentChapter + 1)} disabled={transitioning} className="flex items-center gap-2 text-[#00C8E0] hover:text-white text-sm disabled:opacity-50" data-testid="next-chapter">
                Next Chapter <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button onClick={goBack} disabled={transitioning} className="flex items-center gap-2 text-emerald-400 hover:text-white text-sm disabled:opacity-50" data-testid="back-to-contents">
                Back to Contents <BookOpen className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </QMSLayout>
    );
  }

  return null;
};

export default EBookReaderPage;
